<script lang="ts">
export default {
  name: 'ExtractedFieldsList',
};
</script>

<script setup lang="ts">
import { ref, computed, toRef, watch } from 'vue';
import { DateTime } from 'luxon';
import type { ParsedField } from '@/types/field';
import type { ReviewPreviewResponse } from '@/types/ai';
import Badge from '@/components/ui/Badge.vue';
import EditableField from '@/components/document/EditableField.vue';
import { useFieldOverrides } from '@/composables/useFieldOverrides';
import { aiService } from '@/services/AIService';
import {
  List,
  Search,
  X,
  AlertTriangle,
  ChevronRight,
  History,
  Loader2,
  Pencil,
  ChevronDown,
  ChevronUp,
  CheckCircle,
} from 'lucide-vue-next';
import { CollapsibleRoot, CollapsibleContent, CollapsibleTrigger } from 'reka-ui';

const props = defineProps<{
  fields: ParsedField[];
  selectedField: ParsedField | null;
  error?: string;
  rawOutput?: string;
  promptResultId?: number;
  overriddenFields?: string[]; // List of field paths that have overrides (from merged endpoint)
}>();

const isMalformed = computed(() => !!props.error && !!props.rawOutput);

const emit = defineEmits<{
  'field-selected': [field: ParsedField];
  'overrides-changed': []; // Emitted when an override is saved or reverted
  'review-completed': []; // Emitted when user finishes a review
}>();

// Review mode state using composable
const promptResultIdRef = toRef(props, 'promptResultId');
const {
  isReviewMode,
  isLoading: isLoadingOverrides,
  isSavingAll,
  overrides,
  overrideCount,
  hasPendingChanges,
  pendingChangeCount,
  enterReviewMode,
  exitReviewMode,
  getOverride,
  hasOverride,
  isPending,
  getError,
  clearError,
  setLocalOverride,
  revertLocalOverride,
  hasLocalChange,
  clearPendingChanges,
  saveAllPendingOverrides,
} = useFieldOverrides(promptResultIdRef as any);

const searchQuery = ref('');
const isHistoryExpanded = ref(false);
const isFinishingReview = ref(false);
const showDiscardDialog = ref(false);
const showConfirmModal = ref(false);
const reviewPreview = ref<ReviewPreviewResponse | null>(null);
const isLoadingPreview = ref(false);
const reviewError = ref<string | null>(null);

watch(promptResultIdRef, () => {
  reviewError.value = null;
});

// Finish review: save pending changes, fetch preview, and show confirmation if needed
const finishReview = async () => {
  if (!props.promptResultId) {
    exitReviewMode();
    return;
  }

  isFinishingReview.value = true;
  reviewError.value = null;
  try {
    // Save all pending local changes first
    if (hasPendingChanges.value) {
      const saveSuccess = await saveAllPendingOverrides();
      if (!saveSuccess) {
        console.error('Some overrides failed to save');
        // Don't exit review mode if saves failed - let user see errors
        return;
      }
      emit('overrides-changed');
    }

    // Fetch preview to check for policy-level changes
    isLoadingPreview.value = true;
    let preview: ReviewPreviewResponse | null = null;
    try {
      preview = await aiService.getReviewPreview(props.promptResultId);
    } catch {
      reviewError.value = 'No matching policy found';
    }
    isLoadingPreview.value = false;

    if (preview && preview.changes?.length > 0) {
      // Show confirmation modal with diff
      reviewPreview.value = preview;
      showConfirmModal.value = true;
    } else {
      // No policy changes - proceed directly
      await completeReview();
    }
  } catch (error) {
    console.error('Failed to finish review:', error);
    isLoadingPreview.value = false;
  } finally {
    isFinishingReview.value = false;
  }
};

// Complete the review: mark as reviewed, emit events, exit review mode, close modal
const completeReview = async () => {
  if (!props.promptResultId) return;

  isFinishingReview.value = true;
  try {
    await aiService.markAsReviewed(props.promptResultId);
    emit('review-completed');
    exitReviewMode();
    showConfirmModal.value = false;
    reviewPreview.value = null;
  } catch (error) {
    console.error('Failed to complete review:', error);
  } finally {
    isFinishingReview.value = false;
  }
};

// Cancel the confirmation modal and stay in review mode
const cancelConfirmModal = () => {
  showConfirmModal.value = false;
  reviewPreview.value = null;
};

// Format a preview value for display
const formatPreviewValue = (value: string | number | boolean | null): string => {
  if (value === null || value === undefined) return '--';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};

// Cancel review: check for unsaved changes and show dialog if needed
const cancelReview = () => {
  reviewError.value = null;
  if (hasPendingChanges.value) {
    showDiscardDialog.value = true;
  } else {
    exitReviewMode();
  }
};

// Discard unsaved changes and exit review mode
const discardAndExit = () => {
  clearPendingChanges();
  showDiscardDialog.value = false;
  exitReviewMode();
};

const filteredFields = computed(() => {
  if (!searchQuery.value.trim()) {
    return props.fields;
  }
  const query = searchQuery.value.toLowerCase();
  return props.fields.filter((field) => {
    const nameMatch = field.name.toLowerCase().includes(query);
    const pathMatch = field.path?.toLowerCase().includes(query) || false;
    const valueMatch = formatValue(field.value).toLowerCase().includes(query);
    const descMatch = field.description?.toLowerCase().includes(query) || false;
    return nameMatch || pathMatch || valueMatch || descMatch;
  });
});

// Sorted overrides for history display
const sortedOverrides = computed(() => {
  return Array.from(overrides.value.values()).sort((a, b) => {
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
});

const handleFieldClick = (field: ParsedField) => {
  // Only emit if field has a location and not in review mode editing
  if (field.location) {
    emit('field-selected', field);
  }
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const isSelected = (field: ParsedField) => {
  if (!props.selectedField || !field.location || !props.selectedField.location) return false;
  return props.selectedField?.path === field.path && props.selectedField?.location?.page === field.location?.page;
};

const clearSearch = () => {
  searchQuery.value = '';
};

// Get type badge color based on field type
const getTypeBadgeClass = (type?: string) => {
  switch (type) {
    case 'string':
      return 'tw:bg-green-100 tw:text-green-700 tw:border-green-200';
    case 'number':
      return 'tw:bg-blue-100 tw:text-blue-700 tw:border-blue-200';
    case 'boolean':
      return 'tw:bg-purple-100 tw:text-purple-700 tw:border-purple-200';
    case 'object':
      return 'tw:bg-amber-100 tw:text-amber-700 tw:border-amber-200';
    case 'array':
      return 'tw:bg-pink-100 tw:text-pink-700 tw:border-pink-200';
    default:
      return 'tw:bg-slate-100 tw:text-slate-600 tw:border-slate-200';
  }
};

// Check if field is a container (object/array without value)
const isContainerField = (field: ParsedField) => {
  return (field.type === 'object' || field.type === 'array') && field.value === undefined;
};

// Check if a field has an override or local change
const isFieldOverridden = (fieldPath: string): boolean => {
  // In review mode, check both saved overrides and pending local changes
  if (isReviewMode.value) {
    return hasOverride(fieldPath) || hasLocalChange(fieldPath);
  }
  // Otherwise, use the overriddenFields prop from the merged endpoint
  return props.overriddenFields?.includes(fieldPath) ?? false;
};

// Check if field has an unsaved local change (for amber styling)
const isFieldLocallyChanged = (fieldPath: string): boolean => {
  return hasLocalChange(fieldPath);
};

// Get the count of overrides (combines local state with prop)
const totalOverrideCount = computed(() => {
  if (isReviewMode.value) {
    return overrideCount.value;
  }
  return props.overriddenFields?.length ?? 0;
});

// Get the display value for a field (considering overrides)
const getDisplayValue = (field: ParsedField): unknown => {
  if (!field.path) return field.value;
  const override = getOverride(field.path);
  if (override) {
    return override.overrideValue;
  }
  return field.value;
};

// Handle override value updates (stores locally, saves on Finish Review)
const handleValueUpdate = (field: ParsedField, value: string | number | boolean) => {
  if (!field.path) return;
  setLocalOverride(field.path, value);
};

// Handle revert - restores field to original value (before any override)
const handleRevert = (field: ParsedField) => {
  if (!field.path) return;

  const override = getOverride(field.path);
  if (override && override.originalValue !== null && override.originalValue !== undefined) {
    // Set local change to original value (will override the saved override on Finish Review)
    setLocalOverride(field.path, override.originalValue);
  } else {
    // Just remove the local change
    revertLocalOverride(field.path);
  }
};

// Handle clear error
const handleClearError = (field: ParsedField) => {
  if (!field.path) return;
  clearError(field.path);
};

// Format date for history
const formatDate = (dateString: string) => {
  return DateTime.fromISO(dateString).toLocaleString(DateTime.DATETIME_SHORT);
};

// Format override value for display
const formatOverrideValue = (value: string | number | boolean | null): string => {
  if (value === null) return 'N/A';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};
</script>

<template>
  <div class="tw:flex tw:h-full tw:min-h-0 tw:flex-col tw:overflow-hidden">
    <!-- Header -->
    <div class="tw:flex tw:shrink-0 tw:flex-col tw:gap-2 tw:border-b tw:border-slate-200 tw:p-4">
      <div class="tw:flex tw:items-center tw:gap-2">
        <List :class="['tw:h-5 tw:w-5', isMalformed ? 'tw:text-amber-600' : 'tw:text-blue-600']" />
        <h3 class="tw:font-semibold tw:text-slate-900">Extracted Fields</h3>
        <Badge
          v-if="isMalformed"
          variant="secondary"
          class="tw:ml-auto tw:border-amber-300 tw:bg-amber-100 tw:px-2 tw:py-0.5 tw:text-xs tw:text-amber-700">
          <AlertTriangle class="tw:mr-1 tw:inline-block tw:h-3 tw:w-3" />
          Malformed
        </Badge>
        <template v-else-if="fields.length > 0">
          <!-- Override count badge -->
          <Badge
            v-if="totalOverrideCount > 0"
            variant="secondary"
            class="tw:border-green-300 tw:bg-green-100 tw:px-2 tw:py-0.5 tw:text-xs tw:text-green-700">
            <Pencil class="tw:mr-1 tw:inline-block tw:h-3 tw:w-3" />
            {{ totalOverrideCount }} override{{ totalOverrideCount > 1 ? 's' : '' }}
          </Badge>
          <Badge
            variant="secondary"
            class="tw:ml-auto tw:px-2 tw:py-0.5 tw:text-xs">
            {{ filteredFields.length }}/{{ fields.length }}
          </Badge>
        </template>
      </div>

      <!-- Review Mode Button (only when promptResultId is available and fields exist) -->
      <div
        v-if="promptResultId && fields.length > 0 && !isMalformed"
        class="tw:flex tw:items-center tw:gap-2">
        <!-- Review button (when not in review mode) -->
        <button
          v-if="!isReviewMode"
          @click="enterReviewMode"
          :disabled="isLoadingOverrides"
          class="tw:btn tw:btn-sm tw:btn-primary tw:gap-1">
          <Loader2
            v-if="isLoadingOverrides"
            class="tw:h-4 tw:w-4 tw:animate-spin" />
          <Pencil
            v-else
            class="tw:h-4 tw:w-4" />
          Review
        </button>
        <!-- Finish Review and Cancel buttons (when in review mode) -->
        <template v-else>
          <button
            @click="cancelReview"
            :disabled="isFinishingReview || isSavingAll"
            class="tw:btn tw:btn-sm tw:btn-ghost tw:gap-1">
            Cancel
          </button>
          <button
            @click="finishReview"
            :disabled="isFinishingReview || isSavingAll"
            class="tw:btn tw:btn-sm tw:btn-success tw:gap-1">
            <Loader2
              v-if="isFinishingReview || isSavingAll"
              class="tw:h-4 tw:w-4 tw:animate-spin" />
            <CheckCircle
              v-else
              class="tw:h-4 tw:w-4" />
            Finish Review
            <Badge
              v-if="pendingChangeCount > 0"
              variant="secondary"
              class="tw:ml-1 tw:border-amber-300 tw:bg-amber-100 tw:px-1.5 tw:py-0 tw:text-[10px] tw:text-amber-700">
              {{ pendingChangeCount }} unsaved
            </Badge>
          </button>
        </template>
      </div>
      <!-- Review error message -->
      <div
        v-if="reviewError"
        class="tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-red-300 tw:bg-red-50 tw:px-3 tw:py-2 tw:text-sm tw:text-red-700">
        <AlertTriangle class="tw:h-4 tw:w-4 tw:shrink-0" />
        {{ reviewError }}
      </div>
    </div>

    <!-- Search Input -->
    <div
      v-if="fields.length > 0 && !isMalformed"
      class="tw:shrink-0 tw:border-b tw:border-slate-200 tw:px-4 tw:py-2">
      <div class="tw:relative">
        <Search class="tw:absolute tw:left-2.5 tw:top-1/2 tw:h-4 tw:w-4 tw:-translate-y-1/2 tw:text-slate-400" />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search fields..."
          @keydown.escape="clearSearch"
          class="tw:w-full tw:rounded-md tw:border tw:border-slate-200 tw:bg-white tw:py-1.5 tw:pl-8 tw:pr-8 tw:text-sm tw:placeholder-slate-400 tw:outline-none tw:transition-colors focus:tw:border-blue-500 focus:tw:ring-1 focus:tw:ring-blue-500" />
        <button
          v-if="searchQuery"
          @click="clearSearch"
          class="tw:absolute tw:right-2 tw:top-1/2 tw:-translate-y-1/2 tw:rounded tw:p-0.5 tw:text-slate-400 tw:transition-colors tw:hover:bg-slate-100 tw:hover:text-slate-600">
          <X class="tw:h-4 tw:w-4" />
        </button>
      </div>
    </div>

    <!-- Override History (collapsible, shown when there are overrides) -->
    <CollapsibleRoot
      v-if="isReviewMode && overrideCount > 0"
      v-model:open="isHistoryExpanded"
      class="tw:shrink-0 tw:border-b tw:border-slate-200">
      <CollapsibleTrigger
        class="tw:flex tw:w-full tw:items-center tw:justify-between tw:bg-slate-50 tw:px-4 tw:py-2 tw:text-left tw:transition-colors tw:hover:bg-slate-100">
        <div class="tw:flex tw:items-center tw:gap-2">
          <History class="tw:h-4 tw:w-4 tw:text-slate-500" />
          <span class="tw:text-sm tw:font-medium tw:text-slate-700">Override History</span>
          <Badge
            variant="secondary"
            class="tw:px-1.5 tw:py-0 tw:text-[10px]">
            {{ overrideCount }}
          </Badge>
        </div>
        <ChevronDown
          v-if="!isHistoryExpanded"
          class="tw:h-4 tw:w-4 tw:text-slate-400" />
        <ChevronUp
          v-else
          class="tw:h-4 tw:w-4 tw:text-slate-400" />
      </CollapsibleTrigger>
      <CollapsibleContent class="tw:max-h-[200px] tw:overflow-y-auto tw:bg-slate-50 tw:px-4 tw:pb-3">
        <div class="tw:space-y-2">
          <div
            v-for="override in sortedOverrides"
            :key="override.id"
            class="tw:rounded-md tw:border tw:border-slate-200 tw:bg-white tw:p-2 tw:text-xs">
            <div class="tw:mb-1 tw:flex tw:items-center tw:justify-between">
              <span class="tw:font-medium tw:text-slate-900">{{ override.fieldPath }}</span>
              <span class="tw:text-slate-400">{{ formatDate(override.updatedAt) }}</span>
            </div>
            <div class="tw:flex tw:items-center tw:gap-2">
              <span class="tw:text-red-600 tw:line-through">{{ formatOverrideValue(override.originalValue) }}</span>
              <span class="tw:text-slate-400">&rarr;</span>
              <span class="tw:font-medium tw:text-green-600">{{ formatOverrideValue(override.overrideValue) }}</span>
            </div>
            <div class="tw:mt-1 tw:text-slate-400">
              by {{ override.userEmail }}
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </CollapsibleRoot>

    <!-- Malformed JSON State -->
    <div
      v-if="isMalformed"
      class="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:overflow-hidden">
      <!-- Error Banner -->
      <div class="tw:mx-4 tw:mt-2 tw:flex tw:shrink-0 tw:items-start tw:gap-2 tw:rounded-lg tw:border tw:border-amber-300 tw:bg-amber-50 tw:p-3">
        <AlertTriangle class="tw:mt-0.5 tw:h-5 tw:w-5 tw:shrink-0 tw:text-amber-600" />
        <div>
          <p class="tw:text-sm tw:font-medium tw:text-amber-800">Malformed Output</p>
          <p class="tw:mt-0.5 tw:text-xs tw:text-amber-700">
            The AI output could not be parsed as structured fields. Raw output shown below for reference.
          </p>
        </div>
      </div>

      <!-- Raw Output View -->
      <div class="tw:mt-3 tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:px-4 tw:pb-4">
        <div class="tw:mb-2 tw:flex tw:shrink-0 tw:items-center tw:gap-2">
          <span class="tw:text-xs tw:font-medium tw:uppercase tw:tracking-wide tw:text-slate-500">Raw Output</span>
          <Badge
            variant="secondary"
            class="tw:bg-amber-100 tw:px-2 tw:py-0.5 tw:text-xs tw:text-amber-700">
            Read Only
          </Badge>
        </div>
        <div class="tw:min-h-0 tw:flex-1 tw:overflow-auto tw:rounded-lg tw:border tw:border-slate-300 tw:bg-slate-50">
          <pre class="tw:whitespace-pre-wrap tw:break-words tw:p-3 tw:font-mono tw:text-xs tw:text-slate-700">{{ rawOutput }}</pre>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="fields.length === 0"
      class="tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:p-4 tw:text-slate-400">
      <p class="tw:text-center tw:text-sm">No extracted fields available</p>
    </div>

    <!-- No Results State -->
    <div
      v-else-if="filteredFields.length === 0"
      class="tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:p-4 tw:text-slate-400">
      <Search class="tw:mb-2 tw:h-8 tw:w-8" />
      <p class="tw:text-center tw:text-sm">No fields match "{{ searchQuery }}"</p>
      <button
        @click="clearSearch"
        class="tw:mt-2 tw:text-sm tw:text-blue-600 tw:hover:underline">
        Clear search
      </button>
    </div>

    <!-- Fields List -->
    <div
      v-else
      class="tw:min-h-0 tw:flex-1 tw:space-y-2 tw:overflow-y-auto tw:p-4">
      <div
        v-for="(field, index) in filteredFields"
        :key="`${field.path || field.name}-${index}`"
        @click="handleFieldClick(field)"
        :style="{ marginLeft: `${(field.depth || 0) * 12}px` }"
        :class="[
          'tw:rounded-lg tw:border tw:p-3 tw:transition-all',
          field.location && !isReviewMode ? 'tw:cursor-pointer' : 'tw:cursor-default',
          isSelected(field)
            ? 'tw:border-blue-500 tw:bg-blue-50 tw:shadow-sm'
            : isContainerField(field)
              ? 'tw:border-slate-300 tw:bg-slate-100'
              : isFieldLocallyChanged(field.path || '')
                ? 'tw:border-amber-300 tw:bg-amber-50'
                : isFieldOverridden(field.path || '')
                  ? 'tw:border-green-300 tw:bg-green-50'
                  : field.location
                    ? 'tw:border-slate-200 tw:bg-slate-50 tw:hover:border-slate-300 tw:hover:bg-slate-100'
                    : 'tw:border-slate-200 tw:bg-slate-50 tw:opacity-75',
        ]">
        <!-- Field Header -->
        <div class="tw:mb-1 tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <!-- Nesting indicator for child fields -->
          <ChevronRight
            v-if="field.depth && field.depth > 0"
            class="tw:h-3 tw:w-3 tw:shrink-0 tw:text-slate-400" />

          <!-- Field name -->
          <span class="tw:text-sm tw:font-semibold tw:text-slate-900">{{ field.name }}</span>

          <!-- Override indicator (amber for unsaved, green for saved) -->
          <span
            v-if="isFieldLocallyChanged(field.path || '')"
            class="tw:h-2 tw:w-2 tw:rounded-full tw:bg-amber-500"
            title="Unsaved change" />
          <span
            v-else-if="isFieldOverridden(field.path || '')"
            class="tw:h-2 tw:w-2 tw:rounded-full tw:bg-green-500"
            title="This field has been overridden" />

          <!-- Type badge -->
          <Badge
            v-if="field.type"
            variant="outline"
            :class="`tw:px-1.5 tw:py-0 tw:text-[10px] ${getTypeBadgeClass(field.type)}`">
            {{ field.type }}
          </Badge>

          <!-- Required indicator -->
          <span
            v-if="field.required"
            class="tw:text-[10px] tw:text-red-500">
            *
          </span>

          <!-- Page badge (only if field has location) -->
          <Badge
            v-if="field.location?.page"
            variant="outline"
            class="tw:ml-auto tw:px-2 tw:py-0.5 tw:text-xs">
            Page {{ field.location.page }}
          </Badge>
        </div>

        <!-- Description (if available) -->
        <p
          v-if="field.description"
          class="tw:mb-1 tw:text-xs tw:text-slate-500 tw:italic">
          {{ field.description }}
        </p>

        <!-- Field Value / Editor -->
        <div v-if="!isContainerField(field)">
          <!-- Editable Field (in review mode for editable types) -->
          <EditableField
            v-if="isReviewMode && field.isEditable && field.path"
            :field="field"
            :override="getOverride(field.path) || null"
            :is-pending="isPending(field.path)"
            :error="getError(field.path) || null"
            :has-local-change="isFieldLocallyChanged(field.path)"
            @update:value="(value) => handleValueUpdate(field, value)"
            @revert="handleRevert(field)"
            @clear-error="handleClearError(field)" />

          <!-- Read-only display (non-review mode or non-editable types) -->
          <div
            v-else
            class="tw:wrap-break-word tw:text-sm tw:text-slate-600">
            {{ formatValue(getDisplayValue(field)) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Discard Changes Dialog -->
    <Teleport to="body">
      <div
        v-if="showDiscardDialog"
        class="tw:fixed tw:inset-0 tw:z-50 tw:flex tw:items-center tw:justify-center tw:bg-black/50"
        @click.self="showDiscardDialog = false">
        <div class="tw:w-full tw:max-w-sm tw:rounded-lg tw:border tw:border-slate-200 tw:bg-white tw:p-6 tw:shadow-xl">
          <div class="tw:mb-4 tw:flex tw:items-center tw:gap-3">
            <div class="tw:flex tw:h-10 tw:w-10 tw:items-center tw:justify-center tw:rounded-full tw:bg-amber-100">
              <AlertTriangle class="tw:h-5 tw:w-5 tw:text-amber-600" />
            </div>
            <div>
              <h3 class="tw:font-semibold tw:text-slate-900">Discard unsaved changes?</h3>
              <p class="tw:text-sm tw:text-slate-500">
                You have {{ pendingChangeCount }} unsaved change{{ pendingChangeCount > 1 ? 's' : '' }}.
              </p>
            </div>
          </div>
          <div class="tw:flex tw:justify-end tw:gap-2">
            <button
              @click="showDiscardDialog = false"
              class="tw:btn tw:btn-sm tw:btn-ghost">
              Keep editing
            </button>
            <button
              @click="discardAndExit"
              class="tw:btn tw:btn-sm tw:btn-error">
              Discard
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Confirm Policy Updates Modal -->
    <Teleport to="body">
      <dialog
        v-if="showConfirmModal && reviewPreview"
        class="tw:modal tw:modal-open">
        <div class="tw:modal-box tw:flex tw:form-control tw:gap-4 tw:max-w-2xl">
          <div class="tw:flex tw:flex-col tw:justify-between">
            <span class="tw:text-2xl tw:pb-2">Confirm Policy Updates</span>
            <span class="tw:text-xs tw:italic tw:text-red-600 tw:font-normal tw:pb-2 tw:w-10/12 tw:ml-5 tw:break-normal">
              *The following changes to policy {{ reviewPreview.policyNumber }} will be updated throughout the Platform.
              Benchmarking, carrier analytics, and client data will be altered.
            </span>
          </div>

          <table class="tw:table tw:table-zebra tw:border-collapse">
            <thead class="tw:font-semibold tw:text-black">
              <tr>
                <th>Field</th>
                <th>Current</th>
                <th>New</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(change, index) in reviewPreview.changes"
                :key="index">
                <td>{{ change.field }}</td>
                <td class="tw:text-red-700">{{ formatPreviewValue(change.currentValue) }}</td>
                <td class="tw:text-green-700">{{ formatPreviewValue(change.newValue) }}</td>
              </tr>
            </tbody>
          </table>

          <div class="tw:flex tw:justify-end tw:mt-20">
            <button
              class="tw:btn tw:btn-sm tw:mr-2"
              :disabled="isFinishingReview"
              @click="cancelConfirmModal">
              Cancel
            </button>
            <button
              class="tw:btn tw:btn-sm tw:btn-primary"
              :disabled="isFinishingReview"
              @click="completeReview">
              <Loader2
                v-if="isFinishingReview"
                class="tw:h-4 tw:w-4 tw:animate-spin" />
              Confirm
            </button>
          </div>
        </div>
      </dialog>
    </Teleport>
  </div>
</template>
